'use strict'

/**
 * Picker-shaped source-lot fixtures for upstream melt demo data.
 * These rows are deliberately separate from master-lot fixtures:
 * they provide stable prototype inventory alongside the live endpoint, which
 * returns real lots from melt complete onward. Purities are already in FE
 * percent form because vuex appends these rows after normalizing live BE rows.
 *
 * When `paidMetals` is non-empty, only lots whose `metals` intersect that set
 * are returned — same compatibility claim the live endpoint + picker subtitle
 * make. `null`/`undefined` returns the full catalog (global inventory).
 * Empty array returns [] (a contract that pays nothing should not surface
 * inventory).
 */

import {fixtureSourceUnitSerial} from '@/util/refinery/upstream/upstreamMasterLot/fixtureSourceUnitSerial'

export const FIXTURE_COLLISION_LOT_ID = 'source-demo-lot-collision'
export const FIXTURE_COLLISION_UNIT_ID = 'source-demo-collision-bar-1'

const buildUnit = ({lotId, lotCode, n, kind = 'bar', weightToz, purity, assayDate = null}) => {
  const sourceBarId = `source-demo-${lotCode.replace(/^RL-/, '').toLowerCase()}-bar-${n}`
  const serial = fixtureSourceUnitSerial({sourceLotCode: lotCode, barNumber: n, kind})
  return {
    id: kind === 'bar' ? sourceBarId : `${sourceBarId}:${kind}`,
    kind,
    sourceBarId,
    sourceLotId: lotId,
    sourceLotCode: lotCode,
    barNumber: n,
    serialNumber: serial,
    sku: serial,
    weight: {troyOunces: weightToz},
    purity,
    assay: {
      id: `${sourceBarId}-assay`,
      type: 'xrf',
      completedAt: assayDate ? `${assayDate}T00:00:00.000Z` : null,
    },
  }
}

const totalWeightToz = (units) => units.reduce(
  (sum, unit) => sum + (Number(unit?.weight?.troyOunces) || 0),
  0,
)

const metalsFor = (units) => {
  const metals = new Set()
  for (const unit of units) {
    for (const [metal, purity] of Object.entries(unit.purity || {})) {
      if (Number.isFinite(purity) && purity > 0) metals.add(metal)
    }
  }
  return [...metals].sort()
}

const buildLot = ({id, lotCode, status, settledAt = null, descriptions, units}) => ({
  id,
  lotCode,
  status,
  settledAt,
  descriptions,
  metals: metalsFor(units),
  totalWeightToz: totalWeightToz(units),
  units,
})

const normalizePaidMetals = (paidMetals) => {
  // null/undefined = unfiltered global inventory.
  if (paidMetals == null) return null
  if (!Array.isArray(paidMetals) || paidMetals.length === 0) return new Set()
  return new Set(paidMetals.map((metal) => String(metal).toLowerCase()))
}

const lotMatchesPaidMetals = (lot, paidMetalSet) => {
  if (paidMetalSet == null) return true
  if (paidMetalSet.size === 0) return false
  return (lot.metals || []).some((metal) => paidMetalSet.has(String(metal).toLowerCase()))
}

export function buildUpstreamSourceLotDemoFixtures(paidMetals) {
  const paidMetalSet = normalizePaidMetals(paidMetals)
  if (paidMetalSet && paidMetalSet.size === 0) return []

  const settledUnits = [
    {
      ...buildUnit({
        lotId: FIXTURE_COLLISION_LOT_ID,
        lotCode: 'RL-2026-S301',
        n: 1,
        weightToz: 212.35,
        purity: {au: 99.91, ag: 0.04, pt: null, pd: null},
        assayDate: '2026-05-11',
      }),
      id: FIXTURE_COLLISION_UNIT_ID,
      sourceBarId: FIXTURE_COLLISION_UNIT_ID,
      assay: {
        id: `${FIXTURE_COLLISION_UNIT_ID}-assay`,
        type: 'xrf',
        completedAt: '2026-05-11T00:00:00.000Z',
      },
    },
    buildUnit({
      lotId: FIXTURE_COLLISION_LOT_ID,
      lotCode: 'RL-2026-S301',
      n: 2,
      weightToz: 187.82,
      purity: {au: 99.87, ag: 0.05, pt: null, pd: null},
      assayDate: '2026-05-11',
    }),
  ]

  const meltCompleteUnits = [
    buildUnit({
      lotId: 'source-demo-lot-melt-complete',
      lotCode: 'RL-2026-M402',
      n: 1,
      weightToz: 254.61,
      purity: {au: 74.2, ag: 12.4, pt: 0.41, pd: 0.36, cu: 8.2},
      assayDate: '2026-05-12',
    }),
    buildUnit({
      lotId: 'source-demo-lot-melt-complete',
      lotCode: 'RL-2026-M402',
      n: 2,
      kind: 'house_pin',
      weightToz: 0.31,
      purity: {au: 72.9, ag: 13.1, pt: 0.38, pd: 0.34, cu: 7.9},
      assayDate: '2026-05-12',
    }),
  ]

  const awaitingApprovalUnits = [
    buildUnit({
      lotId: 'source-demo-lot-awaiting-approval',
      lotCode: 'RL-2026-A503',
      n: 1,
      weightToz: 318.44,
      purity: {au: 56.7, ag: 22.5, pt: 0.12, pd: 0.09},
      assayDate: '2026-05-13',
    }),
    buildUnit({
      lotId: 'source-demo-lot-awaiting-approval',
      lotCode: 'RL-2026-A503',
      n: 2,
      kind: 'customer_pin',
      weightToz: 0.42,
      purity: {au: 55.4, ag: 23.0, pt: 0.1, pd: 0.08},
      assayDate: '2026-05-13',
    }),
  ]

  const draftTopUpUnits = [
    buildUnit({
      lotId: 'source-demo-lot-draft-top-up',
      lotCode: 'RL-2026-0199',
      n: 1,
      weightToz: 100,
      purity: {au: 99.9, ag: null},
      assayDate: '2026-05-04',
    }),
  ]

  return [
    buildLot({
      id: FIXTURE_COLLISION_LOT_ID,
      lotCode: 'RL-2026-S301',
      status: 'settled',
      settledAt: '2026-05-15T16:00:00.000Z',
      descriptions: ['demo settled source lot'],
      units: settledUnits,
    }),
    buildLot({
      id: 'source-demo-lot-melt-complete',
      lotCode: 'RL-2026-M402',
      status: 'melt_completed',
      descriptions: ['demo melt-complete source lot'],
      units: meltCompleteUnits,
    }),
    buildLot({
      id: 'source-demo-lot-awaiting-approval',
      lotCode: 'RL-2026-A503',
      status: 'awaiting_customer_approval',
      descriptions: ['demo awaiting approval source lot'],
      units: awaitingApprovalUnits,
    }),
    buildLot({
      id: 'source-demo-lot-draft-top-up',
      lotCode: 'RL-2026-0199',
      status: 'settled',
      settledAt: '2026-05-04T16:00:00.000Z',
      descriptions: ['high-au top-up for the draft lifecycle walkthrough'],
      units: draftTopUpUnits,
    }),
  ].filter((lot) => lotMatchesPaidMetals(lot, paidMetalSet))
}

export const __testing__ = Object.freeze({
  FIXTURE_COLLISION_LOT_ID,
  FIXTURE_COLLISION_UNIT_ID,
})
